import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { AccessLevelEnum, ampli, TeamMemberInterface } from '@shared/api';
import { environment } from '@shared/environments';
import {
  CustomValidators,
  FormButtonTypeEnum,
  FormComponent,
  FormControlTypeEnum,
  FormService,
  getValidationConfig,
} from '@shared/form';
import { IcuLocalizationService } from '@shared/services';
import { MenuRadioItemInterface } from '@shared/ui/menu';
import { isArray } from 'lodash-es';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomEntityCollectionBaseService } from '../../entity-collection-base.class';
import { EntityTypeEnum } from '../../entity.interface';

@Injectable({
  providedIn: 'root',
})
export class TeamMembersService extends CustomEntityCollectionBaseService<TeamMemberInterface> {
  private readonly http = inject(HttpClient);
  private readonly formService = inject(FormService);
  private readonly toastrService = inject(ToastrService);
  private readonly icuLocalizationService = inject(IcuLocalizationService);

  private readonly teamMembersTableAccessLevelToText = {
    [AccessLevelEnum.ADMINISTRATOR]: $localize`:@@teamMembersTableAdministratorAccessLevelText:Administrator`,
    [AccessLevelEnum.MANAGER]: $localize`:@@teamMembersTableManagerAccessLevelText:Manager`,
    [AccessLevelEnum.OWNER]: $localize`:@@teamMembersTableOwnerAccessLevelText:Owner`,
    [AccessLevelEnum.VIEWER]: $localize`:@@teamMembersTableViewerAccessLevelText:Viewer`,
  };

  readonly accessLevelItems: MenuRadioItemInterface[] = [
    {
      title:
        this.teamMembersTableAccessLevelToText[AccessLevelEnum.ADMINISTRATOR],
      value: AccessLevelEnum.ADMINISTRATOR,
    },
    {
      title: this.teamMembersTableAccessLevelToText[AccessLevelEnum.MANAGER],
      value: AccessLevelEnum.MANAGER,
    },
    {
      title: this.teamMembersTableAccessLevelToText[AccessLevelEnum.VIEWER],
      value: AccessLevelEnum.VIEWER,
    },
    {
      title: this.teamMembersTableAccessLevelToText[AccessLevelEnum.OWNER],
      value: AccessLevelEnum.OWNER,
      hidden: true,
    },
  ];

  constructor(elementsFactory: EntityCollectionServiceElementsFactory) {
    super(EntityTypeEnum.TeamMember, elementsFactory);
  }

  openDeleteTeamMembersModal(
    teamMembers: TeamMemberInterface[],
    teamId: string,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamMembersDeleteTeamMembersModalTitle:Remove team member?`,
        form: {
          ids: ['label'],
          controls: {
            label: {
              title:
                teamMembers.length > 1
                  ? this.icuLocalizationService.format(
                      $localize`:@@teamMembersMultipleDeleteTeamMembersModalLabel:Remove {count, plural, one {<b>#</b> team member} other {<b>#</b> team members}} from the team and revoke their access to sites`,
                      { count: teamMembers.length },
                    )
                  : $localize`:@@teamMembersSingleDeleteTeamMembersModalLabel:Remove <b>${teamMembers[0]!.name}</b> from the team and revoke their access to sites`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.deleteTeamMembers(teamMembers, teamId).pipe(
                tap(() =>
                  this.toastrService.success(
                    teamMembers.length > 1
                      ? this.icuLocalizationService.format(
                          $localize`:@@teamMembersMultipleDeleteTeamMembersSuccessNotification:You have removed {count, plural, one {<b>#</b> team member} other {<b>#</b> team members}} from the team`,
                          { count: teamMembers.length },
                        )
                      : $localize`:@@teamMembersSingleDeleteTeamMembersSuccessNotification:You have removed <b>${teamMembers[0]!.name}</b> from the team`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openLeaveTeamModal(
    teamMembers: TeamMemberInterface[],
    teamId: string,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamMembersLeaveTeamModalTitle:Leave team?`,
        form: {
          ids: ['label'],
          controls: {
            label: {
              title: $localize`:@@teamMembersLeaveTeamModalText:Leave the team and lose access to sites`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.deleteTeamMembers(teamMembers, teamId).pipe(
                tap(() =>
                  this.toastrService.success(
                    $localize`:@@teamMembersLeaveTeamSuccessNotification:You have left the team`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openChangeTeamMembersRole(
    teamMembers: TeamMemberInterface[],
    teamId: string,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      height: '290px',
      data: {
        title: $localize`:@@teamMembersChangeRolesModalTitle:Change role`,
        form: {
          ids: ['label', 'select'],
          controls: {
            label: {
              title:
                teamMembers.length > 1
                  ? this.icuLocalizationService.format(
                      $localize`:@@teamMembersMultipleChangeRolesModalLabel:Change the role of {count, plural, one {<b>#</b> team member} other {<b>#</b> team members}}`,
                      { count: teamMembers.length },
                    )
                  : $localize`:@@teamMembersSingleChangeRolesModalLabel:Change the role of a team member`,
              type: FormControlTypeEnum.LABEL,
            },
            select: {
              placeholder: $localize`:@@teamMembersChangeRolesModalSelectPlaceholder:Select a role`,
              options: this.accessLevelItems.filter((item) => !item.hidden),
              value:
                teamMembers.length === 1 ? teamMembers[0]!.accessLevel : '',
              type: FormControlTypeEnum.SELECT,
              validation:
                teamMembers.length === 1
                  ? getValidationConfig([
                      CustomValidators.required(),
                      CustomValidators.changed(teamMembers[0]!.accessLevel),
                    ])
                  : getValidationConfig([CustomValidators.required()]),
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: (value: { select: MenuRadioItemInterface }) =>
              this.changeTeamMemberRole(
                teamMembers,
                value.select.value as AccessLevelEnum,
                teamId,
              ).pipe(
                tap(() =>
                  this.toastrService.success(
                    teamMembers.length > 1
                      ? this.icuLocalizationService.format(
                          $localize`:@@teamMembersMultipleChangeRoleTeamMembersSuccessNotification:You have changed roles for {count, plural, one {<b>#</b> team member} other {<b>#</b> team members}}`,
                          { count: teamMembers.length },
                        )
                      : $localize`:@@teamMembersSingleChangeRoleTeamMembersSuccessNotification:You have changed the role for team member <b>${teamMembers[0]!.name}</b>`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openInviteMemberModal(teamGuid: string): void {
    this.formService.openForm({
      data: {
        title: $localize`:@@inviteMemberModalTitle:Invite team member`,
        form: {
          ids: ['label', 'accessLevel'],
          controls: {
            label: {
              title: $localize`:@@inviteMemberModalText:Select the role of a team member`,
              type: FormControlTypeEnum.LABEL,
            },
            accessLevel: {
              placeholder: $localize`:@@inviteMemberModalSelectPlaceholder:Select a role`,
              validation: getValidationConfig([CustomValidators.required()]),
              value: AccessLevelEnum.ADMINISTRATOR,
              options: this.accessLevelItems.filter((item) => !item.hidden),
              type: FormControlTypeEnum.SELECT,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: (value: { accessLevel: MenuRadioItemInterface }) => {
              return this.createInvite(
                teamGuid,
                value.accessLevel.value as AccessLevelEnum,
              ).pipe(
                tap((response) => {
                  this.openCopyInviteMemberModal(response.invitationToken);
                }),
              );
            },
          },
        ],
      },
    });
  }

  private openCopyInviteMemberModal(inviteMemberGuid: string): void {
    this.formService.openForm({
      data: {
        title: $localize`:@@inviteMemberModalCopyLinkModalTitle:Invite team member`,
        form: {
          ids: ['label', 'link', 'info'],
          controls: {
            label: {
              title: $localize`:@@inviteMemberModalCopyLinkModalText:Copy and share this link with a new team member`,
              type: FormControlTypeEnum.LABEL,
            },
            link: {
              title: inviteMemberGuid
                ? `${window.location.protocol}//${window.location.host}/invite/${inviteMemberGuid}`
                : '',
              type: FormControlTypeEnum.INPUT_COPY,
            },
            info: {
              title: $localize`:@@inviteMemberCopyLinkModalInfo:Please note that users will appear in the member list after confirming the invitation via a link valid for 72 hours`,
              type: FormControlTypeEnum.INFO,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.PRIMARY,
            title: $localize`:@@inviteMemberCopyLinkModalConfirmButton:Copy link`,
            action: (value: any, controls) => {
              if (controls && controls['link']) {
                controls['link'].value = true;
              }
              return of({ ignoreClose: true });
            },
          },
        ],
      },
    });
  }

  createInvite(
    teamGuid: string,
    newAccessLevel: AccessLevelEnum,
  ): Observable<{ invitationToken: string }> {
    return this.http
      .post<{ invitationToken: string }>(
        `${environment.apiHost}/team_v2/${teamGuid}/create-invite`,
        {
          accessLevel: newAccessLevel,
        },
      )
      .pipe(
        tap(() => {
          ampli.teamMemberInvited({
            team_id: teamGuid,
            invite_method: 'link',
            role_assigned: newAccessLevel,
          });
          this.refresh();
        }),
      );
  }

  changeTeamMemberRole(
    teamMembers: TeamMemberInterface | TeamMemberInterface[],
    newRole: AccessLevelEnum,
    teamId: string,
  ): Observable<TeamMemberInterface> {
    if (isArray(teamMembers) && teamMembers.length > 1) {
      return this.update(
        { accessLevel: newRole as AccessLevelEnum },
        { isOptimistic: true },
        teamMembers.map((member) => member.guid),
      );
    } else {
      const teamMember = isArray(teamMembers) ? teamMembers[0] : teamMembers;

      if (teamMember) {
        return this.update(
          { accessLevel: newRole as AccessLevelEnum },
          { isOptimistic: true },
          [teamMember.guid],
        ).pipe(
          tap(() =>
            ampli.roleChanged({
              old_role: teamMember.accessLevel,
              new_role: newRole as AccessLevelEnum,
              team_id: teamId,
            }),
          ),
        );
      } else {
        return EMPTY;
      }
    }
  }

  deleteTeamMembers(
    teamMembers: TeamMemberInterface | TeamMemberInterface[],
    teamId: string,
  ): Observable<string | number> {
    return this.delete(teamMembers).pipe(
      tap(() => {
        ampli.teamMemberRemoved({
          removed_user_id_array: isArray(teamMembers)
            ? teamMembers.map((teamMember) => teamMember.guid)
            : [teamMembers.guid],
          team_id: teamId,
        });
      }),
    );
  }
}
